export const GROUP_AND = 'and';
export const GROUP_OR = 'or';

export const VALUELESS_OPS = ['exists', 'notExists'];

export const OPERATORS = [
  { value: 'equals', label: 'equals' },
  { value: 'notEquals', label: 'is not' },
  { value: 'contains', label: 'contains' },
  { value: 'startsWith', label: 'starts with' },
  { value: 'in', label: 'is any of (comma-separated)' },
  { value: 'exists', label: 'is present' },
  { value: 'notExists', label: 'is missing' },
  { value: 'greaterThan', label: 'greater than' },
  { value: 'lessThan', label: 'less than' },
  { value: 'greaterThanOrEqual', label: 'on or after' },
  { value: 'lessThanOrEqual', label: 'on or before' }
];

export function operatorLabel(op) {
  return OPERATORS.find((o) => o.value === op)?.label || op || '';
}

export const EQUALITY_ONLY_OPS = ['equals', 'in'];

const AUTO_OPS = ['equals', 'contains'];

export function isOperatorAllowed(op, fieldMeta) {
  if (!fieldMeta?.equalityOnly) return true;
  return EQUALITY_ONLY_OPS.includes(op);
}

export function defaultOperatorForField(fieldMeta) {
  if (!fieldMeta) return 'equals';
  if (fieldMeta.equalityOnly) return 'equals';
  if (Array.isArray(fieldMeta.valueOptions) && fieldMeta.valueOptions.length) {
    return 'equals';
  }
  return fieldMeta.type === 'String' ? 'contains' : 'equals';
}

export function reconcileOperator(currentOp, fieldMeta) {
  if (!isOperatorAllowed(currentOp, fieldMeta)) {
    return defaultOperatorForField(fieldMeta);
  }
  if (AUTO_OPS.includes(currentOp)) return defaultOperatorForField(fieldMeta);
  return currentOp;
}

export function isGroup(node) {
  return (
    !!node &&
    typeof node === 'object' &&
    !Array.isArray(node) &&
    Array.isArray(node.children) &&
    (node.op === GROUP_AND || node.op === GROUP_OR)
  );
}

export function emptyGroup(op = GROUP_AND) {
  return { op, children: [] };
}

export function newCriterion(model = '') {
  return { model, field: '', op: 'equals', value: '' };
}

export function emptyCriteriaTree(model = '') {
  return {
    op: GROUP_AND,
    children: [{ op: GROUP_AND, children: [newCriterion(model)] }]
  };
}

export function toCriteriaTree(value) {
  if (isGroup(value)) {
    const children = value.children.map((child) =>
      isGroup(child) ? child : null
    );
    if (children.every(Boolean)) return value;
    return {
      op: value.op,
      children: [{ op: GROUP_AND, children: value.children }]
    };
  }
  if (Array.isArray(value) && value.length > 0) {
    return { op: GROUP_AND, children: [{ op: GROUP_AND, children: value }] };
  }
  return emptyGroup();
}

/** Every criterion in the tree, in display order. */
export function criteriaList(tree) {
  const root = toCriteriaTree(tree);
  return root.children.flatMap((group) => group.children || []);
}

export function isCriterionComplete(criterion) {
  if (!criterion || !criterion.model || !criterion.field || !criterion.op) {
    return false;
  }
  if (VALUELESS_OPS.includes(criterion.op)) return true;
  return (
    criterion.value !== '' &&
    criterion.value !== undefined &&
    criterion.value !== null
  );
}

export function countCompleteCriteria(tree) {
  return criteriaList(tree).filter(isCriterionComplete).length;
}

export function filterCriteria(tree, keep) {
  const root = toCriteriaTree(tree);
  const groups = root.children
    .map((group) => ({
      ...group,
      children: (group.children || []).filter(keep)
    }))
    .filter((group) => group.children.length > 0);
  return { op: root.op, children: groups };
}

export function prunedCriteriaTree(tree) {
  const pruned = filterCriteria(tree, isCriterionComplete);
  return pruned.children.length > 0 ? pruned : null;
}

export function criteriaSignature(tree) {
  return JSON.stringify(prunedCriteriaTree(tree));
}

export function updateCriterion(tree, groupIndex, criterionIndex, next) {
  const root = toCriteriaTree(tree);
  return {
    ...root,
    children: root.children.map((group, gi) =>
      gi !== groupIndex
        ? group
        : {
            ...group,
            children: group.children.map((criterion, ci) =>
              ci === criterionIndex ? next : criterion
            )
          }
    )
  };
}

export function addCriterion(tree, groupIndex, model) {
  const root = toCriteriaTree(tree);
  if (!root.children[groupIndex]) return root;
  return {
    ...root,
    children: root.children.map((group, gi) =>
      gi !== groupIndex
        ? group
        : { ...group, children: [...group.children, newCriterion(model)] }
    )
  };
}

export function addGroup(tree, model) {
  const root = toCriteriaTree(tree);
  return {
    ...root,
    children: [
      ...root.children,
      { op: GROUP_AND, children: [newCriterion(model)] }
    ]
  };
}

export function removeCriterion(tree, groupIndex, criterionIndex) {
  const root = toCriteriaTree(tree);
  const groups = root.children
    .map((group, gi) =>
      gi !== groupIndex
        ? group
        : {
            ...group,
            children: group.children.filter((_, ci) => ci !== criterionIndex)
          }
    )
    .filter((group) => group.children.length > 0);
  return { ...root, children: groups };
}

export function removeGroup(tree, groupIndex) {
  const root = toCriteriaTree(tree);
  return {
    ...root,
    children: root.children.filter((_, gi) => gi !== groupIndex)
  };
}

export function setGroupOp(tree, groupIndex, op) {
  const root = toCriteriaTree(tree);
  return {
    ...root,
    children: root.children.map((group, gi) =>
      gi === groupIndex ? { ...group, op } : group
    )
  };
}

export function setRootOp(tree, op) {
  return { ...toCriteriaTree(tree), op };
}
